import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAdminSession, loginAdmin, logoutAdmin } from "@/lib/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [csrfToken, setCsrfToken] = useState("");

  const refreshSession = useCallback(async () => {
    setStatus("loading");
    try {
      const session = await getAdminSession();
      setCsrfToken(session.csrfToken);
      setStatus("authenticated");
    } catch (error) {
      setCsrfToken("");
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        setStatus("unauthenticated");
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const onUnauthorized = () => {
      setCsrfToken("");
      setStatus("unauthenticated");
    };
    window.addEventListener("silapdi:unauthorized", onUnauthorized);
    return () => window.removeEventListener("silapdi:unauthorized", onUnauthorized);
  }, []);

  const login = useCallback(async (username, password) => {
    const session = await loginAdmin(username, password);
    setCsrfToken(session.csrfToken);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await logoutAdmin(csrfToken);
    setCsrfToken("");
    setStatus("unauthenticated");
  }, [csrfToken]);

  const value = useMemo(() => ({
    status,
    login,
    logout,
    refreshSession,
  }), [status, login, logout, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth harus digunakan di dalam AuthProvider.");
  return value;
}
